import {Component, forwardRef, Input, OnDestroy, OnInit} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import gql from 'graphql-tag';
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';
import {ConfirmationComponent} from '../../modals/confirmation/confirmation.component';
import {catchError, EMPTY, Subject, Subscription, switchMap} from 'rxjs';
import {Apollo} from 'apollo-angular';
import {ErrorService} from '../../services/error.service';
import {
  TagLabelItem,
  TagLabelTitleComponent,
} from '../../modals/tag-label-title/tag-label-title.component';

export const labelFrag = gql`
  fragment MultiSelectComponentLabelFragment on LabelType {
    id
    name
    viewAllContent
  }
`;

export const tagFrag = gql`
  fragment MultiSelectComponentTagFragment on TagType {
    id
    name
  }
`;

export const deleteLabelMutation = gql`
  mutation DeleteLabel($id: ID) {
    deleteLabel(labelId: $id) {
      error
    }
  }
`;

export const deleteTagMutation = gql`
  mutation DeleteTag($id: ID) {
    deleteTag(tagId: $id) {
      error
    }
  }
`;

export interface TagLabelItemWithCheck extends TagLabelItem {
  checked: boolean;
}

export enum MultiSelectType {
  LABEL,
  TAG,
}

@Component({
  selector: 'app-multi-select',
  templateUrl: './multi-select.component.html',
  styleUrls: ['./multi-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelectComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class MultiSelectComponent implements OnInit, ControlValueAccessor, OnDestroy {
  @Input() multiSelectType: MultiSelectType = MultiSelectType.LABEL;
  MultiSelectType = MultiSelectType;
  @Input() showControlButtons = false;
  labelsWithCheck: TagLabelItemWithCheck[] = [];

  @Input()
  set labels(value: TagLabelItem[]) {
    this.labelsWithCheck = value.map(label => ({
      ...label,
      checked: this.labelsWithCheck.some(l => l.id === label.id && l.checked),
    }));
  }

  tagsWithCheck: (TagLabelItemWithCheck | null)[] = [];

  @Input()
  set tags(value: (TagLabelItem | null)[]) {
    console.log('set tags: ', value);

    this.tagsWithCheck = value.map(tag => {
      if (!tag) {
        return tag;
      }

      return {
        ...tag,
        checked: this.tagsWithCheck.some(t => t?.id === tag.id && t?.checked),
      };
    });
  }

  onChange: ((options: TagLabelItem[]) => void) | undefined;

  @Input() formDisabled = false;
  bsModalRef: BsModalRef | null = null;
  deleteOption$ = new Subject<TagLabelItem>();
  addOrEditOption$ = new Subject<TagLabelItem | null>();
  deletedOption: TagLabelItem | null = null;
  allTagsChecked = false;
  @Input() checkAllOnEmpty = false;

  private subscriptions: Subscription[] = [];

  constructor(
    private modalService: BsModalService,
    private apollo: Apollo,
    private errorService: ErrorService,
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.deleteOption$
        .pipe(
          switchMap(option => {
            this.bsModalRef = this.modalService.show(ConfirmationComponent);
            return this.bsModalRef.content.confirm.pipe(
              switchMap(confirmation => {
                if (confirmation) {
                  const mutation =
                    this.multiSelectType === MultiSelectType.LABEL
                      ? deleteLabelMutation
                      : deleteTagMutation;
                  return this.apollo.mutate({
                    mutation,
                    variables: {
                      id: option.id,
                    },
                    update: cache => {
                      cache.evict({
                        id: 'ROOT_QUERY',
                        fieldName:
                          this.multiSelectType === MultiSelectType.LABEL ? 'labels' : 'tags',
                      });

                      // Would be better to use evicts list of type. But this works.
                      cache.evict({id: 'ROOT_QUERY', fieldName: 'templateItem'});
                    },
                  });
                } else {
                  return EMPTY;
                }
              }),
            );
          }),
        )
        .pipe(
          catchError(err => {
            this.errorService.showErrorDialog(err);
            return EMPTY;
          }),
        )
        .subscribe(() => {
          if (this.multiSelectType === MultiSelectType.LABEL) {
            this.labelsWithCheck = this.labelsWithCheck.filter(
              label => label.id !== this.deletedOption?.id,
            );
            if (this.onChange) {
              this.onChange(
                this.labelsWithCheck.filter(l => l.checked).map(({checked, ...rest}) => rest),
              );
            }
          } else if (this.multiSelectType === MultiSelectType.TAG) {
            this.tagsWithCheck = this.tagsWithCheck.filter(
              tag => tag?.id !== this.deletedOption?.id,
            );
          }
        }),
      this.addOrEditOption$
        .pipe(
          switchMap(option => {
            this.bsModalRef = this.modalService.show(TagLabelTitleComponent, {
              initialState: {option, optionType: this.multiSelectType},
            });
            return this.bsModalRef.content.changedOption;
          }),
        )
        /* eslint-disable @typescript-eslint/no-explicit-any */
        .subscribe((output: any) => {
          if (this.multiSelectType === MultiSelectType.LABEL) {
            if (this.labelsWithCheck.find(l => l.id === output.id)) {
              this.labelsWithCheck = this.labelsWithCheck.map(l => {
                if (l.id === output.id) {
                  l.name = output.name;
                }
                return l;
              });
            } else {
              this.labelsWithCheck.push({...output, checked: false});
            }
          }

          if (this.multiSelectType === MultiSelectType.TAG) {
            if (this.tagsWithCheck.find(t => t?.id === output.id)) {
              this.tagsWithCheck = this.tagsWithCheck.map(t => {
                if (t === null) {
                  return t;
                }
                if (t.id === output.id) {
                  t.name = output.name;
                }
                return t;
              });
            } else {
              this.tagsWithCheck.push({...output, checked: false});
            }
          }
        }),
    );
  }

  /* eslint-disable @typescript-eslint/no-explicit-any */
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  /* eslint-disable @typescript-eslint/no-explicit-any */
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  registerOnTouched(fn: any): void {}

  writeValue(options: TagLabelItem[] | null): void {
    if (this.multiSelectType === MultiSelectType.LABEL) {
      if (this.labelsWithCheck.length) {
        this.labelsWithCheck = this.labelsWithCheck.map(label => {
          label.checked = !!options?.some(option => option.id === label.id);
          return label;
        });
      } else {
        this.labelsWithCheck =
          options?.map(option => ({
            ...option,
            checked: true,
          })) ?? [];
      }

      if (this.checkAllOnEmpty) {
        let labelChecked = false;
        this.labelsWithCheck.forEach(l => {
          labelChecked = labelChecked || l.checked;
        });
        if (!labelChecked) {
          this.labelsWithCheck[0].checked = true;
        }
      }

      if (this.onChange) {
        this.onChange(
          this.labelsWithCheck.filter(l => l.checked).map(({checked, ...rest}) => rest),
        );
      }
    }

    if (this.multiSelectType === MultiSelectType.TAG) {
      if (this.tagsWithCheck.length) {
        this.tagsWithCheck = this.tagsWithCheck.map(tag => {
          if (tag === null) {
            return tag;
          }
          tag.checked = !!options?.some(option => option.id === tag.id);
          return tag;
        });
      } else {
        this.tagsWithCheck =
          options?.map(option => ({
            ...option,
            checked: true,
          })) ?? [];
      }

      if (!this.tagsWithCheck.some(t => t?.checked)) {
        this.allTagsChecked = true;
      }
    }
  }

  checkLabel(label: TagLabelItemWithCheck): void {
    if (label.viewAllContent) {
      this.labelsWithCheck.map(l => {
        if (!l.viewAllContent) {
          l.checked = false;
        }
        return l;
      });
    } else {
      this.labelsWithCheck = this.labelsWithCheck.map(l => {
        if (l.viewAllContent) {
          l.checked = false;
        }
        return l;
      });
    }

    this.labelsWithCheck = this.labelsWithCheck.map(l => {
      if (l.id === label.id) {
        l.checked = !label.checked;
      }
      return l;
    });

    if (this.onChange) {
      this.onChange(this.labelsWithCheck.filter(l => l.checked).map(({checked, ...rest}) => rest));
    }
  }

  checkTag(tag: TagLabelItemWithCheck | null): void {
    if (!tag) {
      this.allTagsChecked = !this.allTagsChecked;
      this.tagsWithCheck.forEach(t => {
        if (t !== null) {
          t.checked = false;
        }
      });
      if (this.onChange) {
        const checkedTags = this.tagsWithCheck.filter(t => t?.checked) as TagLabelItemWithCheck[];
        this.onChange(checkedTags.filter(t => t.checked).map(({checked, ...rest}) => rest));
      }
      return;
    }

    this.allTagsChecked = false;

    this.tagsWithCheck = this.tagsWithCheck.map(t => {
      if (t === null) {
        return t;
      }
      if (t.id === tag.id) {
        t.checked = !tag.checked;
      }
      return t;
    });

    if (this.onChange) {
      const checkedTags = this.tagsWithCheck.filter(t => t?.checked) as TagLabelItemWithCheck[];
      this.onChange(checkedTags.filter(t => t.checked).map(({checked, ...rest}) => rest));
    }
  }

  addOrEditOption(option?: TagLabelItem) {
    this.addOrEditOption$.next(option ?? null);
  }

  deleteOption(option: TagLabelItem): void {
    this.deleteOption$.next(option);
    this.deletedOption = option;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}
